import posthog from "posthog-js";
import { isMobileAppContext } from "./mobile-handoff";

const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? "dev";

function getAppPlatform(): string {
  const ua = navigator.userAgent;
  if (ua.includes("Mac")) return "macOS";
  if (ua.includes("Windows")) return "Windows";
  if (ua.includes("Linux")) return "Linux";
  return "Unknown";
}

/**
 * Is product analytics even a possibility on this page load?
 *
 * False when no key is compiled in (the mobile bundles are built without one —
 * build-web-bundle.yml passes no VITE_POSTHOG_KEY) AND, independently, anywhere
 * that is "the app" to a store reviewer: inside the Capacitor shell, or on the
 * external-browser login leg the shell opens (/login?from=mobile → OAuth →
 * /auth/mobile). The App Store privacy answers say the app collects no usage
 * data; this is what keeps that true even though the login leg is served by
 * the web build, which does carry a key. The consent banner keys off this too,
 * so nobody is asked to consent to analytics that cannot run.
 */
export function analyticsAvailable(): boolean {
  if (!import.meta.env.VITE_POSTHOG_KEY) return false;
  return !isMobileAppContext();
}

export function initPostHog(): void {
  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  const apiHost = import.meta.env.VITE_POSTHOG_HOST ?? "https://us.i.posthog.com";

  if (!apiKey) {
    console.warn("[PostHog] VITE_POSTHOG_KEY not configured — analytics disabled");
    return;
  }
  if (!analyticsAvailable()) {
    // Mobile shell or its login leg: no init at all, so nothing is captured
    // even for a browser that granted consent to the web app earlier.
    console.info("[PostHog] mobile app context — analytics disabled");
    return;
  }

  posthog.init(apiKey, {
    api_host: apiHost,
    ip: false,
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "[data-ph-mask]",
      blockSelector: "[data-ph-block]",
    },
    person_profiles: "identified_only",
    persistence: "localStorage",
    opt_out_capturing_by_default: true,
    // Tier 2 of the consent model: anyone who has not accepted (declined, or
    // not answered the banner yet) is still counted, but cookielessly — no
    // cookie, no local/session storage, no identify, no replay. Identity is a
    // daily-rotating hash computed on PostHog's servers, and every such event
    // carries `$cookieless_mode: true`. Needs "Cookieless server hash mode" on
    // in project settings (it is), or these events are dropped at ingestion.
    cookieless_mode: "on_reject",
    loaded: (ph) => {
      ph.register({
        app_platform: getAppPlatform(),
        app_version: APP_VERSION,
        electron: false,
        surface: "web",
      });
      if (getConsentStatus() === "granted") {
        ph.opt_in_capturing();
      } else {
        // `on_reject` only goes cookieless after an EXPLICIT opt-out; with
        // opt_out_capturing_by_default alone it sends nothing at all
        // (posthog-js#2841). Opting out here is what makes the people who
        // never answer the banner — most of them — show up as counts. It
        // does not touch `ph_consent`, so the banner still asks.
        ph.opt_out_capturing();
      }
    },
  });
}

const INTERNAL_DOMAINS = new Set(["solarpunk.technology", "theubik.com"]);

/** The workflow a path belongs to, for `workflow_viewed`. Null for chat,
 *  settings and other non-workflow screens. Keys match the backend
 *  `<WORKFLOW>_ORGS` gates so usage lines up with what each org has on. */
const WORKFLOW_BY_SEGMENT: Record<string, string> = {
  "packaging-tracker": "packaging_tracker",
  "packaging-specifications": "packaging_spec",
  planning: "planning",
  "customer-planning": "customer_planning",
  replenishment: "replenishment",
  "production-reconciliation": "production_reconciliation",
  capture: "capture",
  csr: "csr",
  "ceo-panel": "ceo_panel",
  "supplier-updates": "supplier_update",
  "document-verification": "docverify",
  shipments: "shipment",
  "ops-status": "ops_status",
  inbox: "inbox",
  meetings: "meetings",
};

export function workflowForPath(pathname: string): string | null {
  const segment = pathname.split("/")[1] ?? "";
  return WORKFLOW_BY_SEGMENT[segment] ?? null;
}

/**
 * Phone-only users (WF10 floor staff) have no email — the field is nullable
 * end to end. Never widen this to accept a phone number: a raw E.164 number is
 * PII we do not send to analytics.
 */
type AnalyticsUser = {
  id: string;
  email: string | null;
  name: string | null;
  orgId?: string | null;
  orgRole?: string | null;
};

/** The signed-in user, kept in memory so accepting the banner mid-session can
 *  identify straight away instead of waiting for the next session check. */
let currentUser: AnalyticsUser | null = null;

export function identifyUser(user: AnalyticsUser): void {
  currentUser = user;
  if (!posthog.__loaded) return;
  // Identity is Tier 3 only. Without consent the SDK is cookieless and would
  // ignore identify anyway; saying so here keeps the rule readable.
  if (getConsentStatus() !== "granted") return;
  const emailDomain = user.email?.split("@")[1]?.toLowerCase() ?? "unknown";
  posthog.identify(user.id, {
    email: user.email,
    name: user.name,
    email_domain: emailDomain,
    is_internal: INTERNAL_DOMAINS.has(emailDomain),
    org_role: user.orgRole ?? null,
    app_platform: getAppPlatform(),
    app_version: APP_VERSION,
    surface: "web",
  });
  // The account. The server sets the group's name/domain on org creation;
  // here we only attach this user's events to it.
  if (user.orgId) posthog.group("organization", user.orgId);
}

export function resetUser(): void {
  currentUser = null;
  if (!posthog.__loaded) return;
  posthog.reset();
}

export function trackEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (!posthog.__loaded) return;
  posthog.capture(event, properties);
}

export function trackPageView(pageName?: string): void {
  if (!posthog.__loaded) return;
  const path = pageName ?? window.location.pathname;
  posthog.capture("$pageview", {
    $current_url: window.location.href,
    page_name: path,
  });
  const workflow = workflowForPath(path);
  if (workflow) posthog.capture("workflow_viewed", { workflow, page_name: path });
}

export function trackScreenTime(pageName: string, durationSeconds: number): void {
  if (!posthog.__loaded) return;
  posthog.capture("screen_time", {
    page_name: pageName,
    duration_seconds: durationSeconds,
  });
}

// `ph_consent` is the visitor's own choice — a strictly-necessary preference
// in the cookie inventory, so it is stored without consent. Storage can throw
// (blocked site data, some WebViews); treat that as "no decision".
function readConsent(): string | null {
  try {
    return localStorage.getItem("ph_consent");
  } catch {
    return null;
  }
}

function writeConsent(value: "granted" | "denied" | null): void {
  try {
    if (value === null) localStorage.removeItem("ph_consent");
    else localStorage.setItem("ph_consent", value);
  } catch {
    // The in-memory SDK state below still applies for this page load.
  }
}

export function grantConsent(): void {
  writeConsent("granted");
  if (!posthog.__loaded) return;
  posthog.opt_in_capturing();
  if (currentUser) identifyUser(currentUser);
}

export function revokeConsent(): void {
  writeConsent("denied");
  // Back to cookieless counts; the SDK clears the identified state itself.
  if (posthog.__loaded) posthog.opt_out_capturing();
}

export function hasConsentDecision(): boolean {
  return readConsent() !== null;
}

export function getConsentStatus(): "granted" | "denied" | null {
  const v = readConsent();
  if (v === "granted") return "granted";
  if (v === "denied") return "denied";
  return null;
}

export function resetConsent(): void {
  writeConsent(null);
  if (posthog.__loaded) posthog.opt_out_capturing();
}

export function captureException(error: unknown): void {
  if (!posthog.__loaded) return;
  const err = error instanceof Error ? error : new Error(String(error));
  posthog.captureException(err);
}

export { posthog };
